import React, { useEffect, useState } from 'react';
import { Container } from 'src/components/layouts/Container/Container';
import CompanySettings from 'src/components/layouts/CompanySettings/CompanySettings';
import CaseSettings from 'src/components/layouts/CaseSettings/CaseSettings';
import ForecastPlanningSettings from 'src/components/layouts/ForecastPlanningSettings/ForecastPlanningSettings';
import ProfileSettings from 'src/components/layouts/ProfileSettings/ProfileSettings';
import FaqHelp from 'src/components/layouts/FaqHelp/FaqHelp';
import WhatsNew from 'src/components/layouts/WhatsNew/WhatsNew';
import classNames from 'classnames';
import { ButtonToneProps } from 'src/components/ui/Button/Button';
import TabBar from 'src/components/layouts/TabBar/TabBar';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import HeadlineWithButtons from 'src/components/ui/HeadlineWithButtons/HeadlineWithButtons';
import { useTranslation } from 'react-i18next';
import { IconName } from 'src/components/ui/Icon/Icon';
import styles from './styles/Settings.module.scss';
import ManageUsers from '../components/layouts/ManageUsers/ManageUsers';
import AgreementsOverview from './AgreementsOverview';
import AddOnsBilling from '../components/layouts/AddOnsBilling/AddOnsBilling';
import McpApiKeySettings from '../components/layouts/McpApiKeySettings/McpApiKeySettings';
import Contacts, { CONTACTS_SETTINGS_PATH } from './Contact';
import { useNavigationItems } from '../hooks/useNavigationItems';

// Define an enum for the tab keys
enum TabKeys {
  MY_PROFILE = 'my_profile',
  COMPANY = 'company',
  CONTACTS = 'contacts',
  CASES = 'cases',
  FORECAST_PLANNING = 'forecast_planning',
  AGREEMENTS = 'agreements',
  // PAYMENT_INVOICES = 'payment_invoices',
  FAQ_HELP = 'faq_help',
  WHATS_NEW = 'whats_new',
  MANAGE_USERS = 'manage_users',
  ADD_ONS_BILLING = 'add_ons_billing',
  MCP_API = 'mcp_api',
}

// Create an object for tab labels
const tabLabels: Record<TabKeys, string> = {
  [TabKeys.MY_PROFILE]: 'settings.my_profile',
  [TabKeys.COMPANY]: 'settings.company',
  [TabKeys.CONTACTS]: 'navbar.contact',
  [TabKeys.CASES]: 'settings.cases.tab',
  [TabKeys.FORECAST_PLANNING]: 'settings.forecast_planning.tab',
  [TabKeys.AGREEMENTS]: 'navbar.agreements',
  // [TabKeys.PAYMENT_INVOICES]: 'settings.payment_invoices',
  [TabKeys.FAQ_HELP]: 'settings.faq_help',
  [TabKeys.WHATS_NEW]: 'changelog.tab',
  [TabKeys.MANAGE_USERS]: 'settings.manage_users',
  [TabKeys.ADD_ONS_BILLING]: 'settings.add_ons_billing',
  [TabKeys.MCP_API]: 'settings.mcp_api.tab',
};

// Define types for the buttons and tabs
interface Button {
  title: string;
  tone: ButtonToneProps;
  icon: IconName;
  onClick: () => void;
}

interface Tab {
  label: string;
  key: TabKeys;
  onClick: () => void;
}

const Settings: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isContactsRoute = location.pathname.startsWith(CONTACTS_SETTINGS_PATH);
  const initialTab = isContactsRoute ? TabKeys.CONTACTS : (searchParams.get('tab') as TabKeys) || TabKeys.MY_PROFILE;
  const [activeTab, setActiveTab] = useState<TabKeys>(initialTab);

  const { t } = useTranslation();

  const buttons: Button[] = [];

  useEffect(() => {
    const currentTab = searchParams.get('tab') as TabKeys;
    if (currentTab && currentTab !== activeTab && Object.values(TabKeys).includes(currentTab)) {
      setActiveTab(currentTab);
    }
  }, [searchParams]);

  const handleTabChange = (tabKey: TabKeys) => {
    setActiveTab(tabKey);
    if (isContactsRoute) {
      navigate(`/settings?tab=${tabKey}`);
      return;
    }
    setSearchParams({ tab: tabKey });
  };

  const { findAllAccessRightsByRoute, findAccessRights } = useNavigationItems();
  const accessRights = findAllAccessRightsByRoute('settings');
  const forecastAccess = findAccessRights('forecasts');
  const hasForecastAccess = !!forecastAccess && (forecastAccess.read || forecastAccess.read_write);
  const contactAccess = findAccessRights('contact');
  const hasContactAccess = !!contactAccess && (contactAccess.read || contactAccess.read_write);

  const tabs: Tab[] = [
    {
      label: t(tabLabels[TabKeys.MY_PROFILE]),
      key: TabKeys.MY_PROFILE,
      onClick: () => handleTabChange(TabKeys.MY_PROFILE),
    },
    {
      label: t(tabLabels[TabKeys.COMPANY]),
      key: TabKeys.COMPANY,
      onClick: () => handleTabChange(TabKeys.COMPANY),
    },
    ...(hasContactAccess
      ? [
          {
            label: t(tabLabels[TabKeys.CONTACTS]),
            key: TabKeys.CONTACTS,
            onClick: () => handleTabChange(TabKeys.CONTACTS),
          },
        ]
      : []),
    {
      label: t(tabLabels[TabKeys.CASES]),
      key: TabKeys.CASES,
      onClick: () => handleTabChange(TabKeys.CASES),
    },
    ...(hasForecastAccess
      ? [
          {
            label: t(tabLabels[TabKeys.FORECAST_PLANNING]),
            key: TabKeys.FORECAST_PLANNING,
            onClick: () => handleTabChange(TabKeys.FORECAST_PLANNING),
          },
        ]
      : []),
    ...(accessRights?.some(r => r.route.includes(TabKeys.AGREEMENTS) && (r.read || r.read_write))
      ? [
          {
            label: t(tabLabels[TabKeys.AGREEMENTS]),
            key: TabKeys.AGREEMENTS,
            onClick: () => handleTabChange(TabKeys.AGREEMENTS),
          },
        ]
      : []),
    // {
    //   label: t(tabLabels[TabKeys.PAYMENT_INVOICES]),
    //   key: TabKeys.PAYMENT_INVOICES,
    //   onClick: () => handleTabChange(TabKeys.PAYMENT_INVOICES),
    // },
    {
      label: t(tabLabels[TabKeys.FAQ_HELP]),
      key: TabKeys.FAQ_HELP,
      onClick: () => handleTabChange(TabKeys.FAQ_HELP),
    },
    {
      label: t(tabLabels[TabKeys.WHATS_NEW]),
      key: TabKeys.WHATS_NEW,
      onClick: () => handleTabChange(TabKeys.WHATS_NEW),
    },
    ...(accessRights?.some(r => r.route.includes(TabKeys.MANAGE_USERS) && (r.read || r.read_write))
      ? [
          {
            label: t(tabLabels[TabKeys.MANAGE_USERS]),
            key: TabKeys.MANAGE_USERS,
            onClick: () => handleTabChange(TabKeys.MANAGE_USERS),
          },
        ]
      : []),

    ...(accessRights?.some(r => r.route.includes(TabKeys.ADD_ONS_BILLING) && (r.read || r.read_write))
      ? [
          {
            label: t(tabLabels[TabKeys.ADD_ONS_BILLING]),
            key: TabKeys.ADD_ONS_BILLING,
            onClick: () => handleTabChange(TabKeys.ADD_ONS_BILLING),
          },
        ]
      : []),

    ...(accessRights?.some(r => r.route.includes(TabKeys.MCP_API) && (r.read || r.read_write))
      ? [
          {
            label: t(tabLabels[TabKeys.MCP_API]),
            key: TabKeys.MCP_API,
            onClick: () => handleTabChange(TabKeys.MCP_API),
          },
        ]
      : []),
  ];
  const effectiveTab = isContactsRoute ? TabKeys.CONTACTS : activeTab;
  const activeIndex = tabs.findIndex(tab => tab.key === effectiveTab);
  const shownTab = activeIndex >= 0 ? effectiveTab : TabKeys.MY_PROFILE;

  return (
    <Container
      title={`${t('navbar.settings')} / ${t(tabLabels[shownTab])}`}
      buttons={buttons}
      className={classNames(styles.root)}>
      <HeadlineWithButtons headline={t('navbar.settings')} buttons={[]} />
      <div className={styles.container}>
        <TabBar defaultIndex={activeIndex < 0 ? 0 : activeIndex} tabs={tabs} />
        {shownTab === TabKeys.MY_PROFILE && <ProfileSettings />}
        {shownTab === TabKeys.COMPANY && <CompanySettings />}
        {hasContactAccess && shownTab === TabKeys.CONTACTS && <Contacts basePath={CONTACTS_SETTINGS_PATH} />}
        {shownTab === TabKeys.CASES && (
          <CaseSettings
            hasWriteAccess={accessRights?.find(right => right.route.includes(TabKeys.CASES))?.read_write ?? true}
          />
        )}
        {hasForecastAccess && shownTab === TabKeys.FORECAST_PLANNING && (
          <ForecastPlanningSettings hasWriteAccess={forecastAccess?.read_write ?? true} />
        )}
        {shownTab === TabKeys.AGREEMENTS && <AgreementsOverview />}
        {shownTab === TabKeys.FAQ_HELP && <FaqHelp />}
        {shownTab === TabKeys.WHATS_NEW && <WhatsNew />}
        {shownTab === TabKeys.MANAGE_USERS && (
          <ManageUsers
            hasWriteAccess={
              accessRights?.find(right => right.route.includes(TabKeys.MANAGE_USERS))?.read_write ?? false
            }
          />
        )}
        {shownTab === TabKeys.ADD_ONS_BILLING && (
          <AddOnsBilling
            hasWriteAccess={
              accessRights?.find(right => right.route.includes(TabKeys.ADD_ONS_BILLING))?.read_write ?? false
            }
          />
        )}
        {shownTab === TabKeys.MCP_API && (
          <McpApiKeySettings
            hasWriteAccess={accessRights?.find(right => right.route.includes(TabKeys.MCP_API))?.read_write ?? false}
          />
        )}
      </div>
    </Container>
  );
};

export default Settings;
